import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Sparkles, Image, Loader2, Lock, Zap, Clock, CheckCircle, X, Award, Send, KeyRound, Star, Download, Heart, ExternalLink } from 'lucide-react';
import VaultLeaderboard from '../components/engagement/VaultLeaderboard';
import TradingMarketplace from '../components/engagement/TradingMarketplace';

// Fixed trophy-colour palette — must match backend TROPHY_COLOURS keys (nft_seed.py)
const TROPHY_COLOURS = [
  { slug: 'gold', label: 'Gold', hex: '#D4A017' },
  { slug: 'silver', label: 'Silver', hex: '#B8BCC4' },
  { slug: 'rose_gold', label: 'Rose Gold', hex: '#B76E79' },
  { slug: 'ruby_red', label: 'Ruby', hex: '#C21E3A' },
  { slug: 'sapphire_blue', label: 'Sapphire', hex: '#1E4FC2' },
  { slug: 'emerald_green', label: 'Emerald', hex: '#0F9D58' },
  { slug: 'amethyst_purple', label: 'Amethyst', hex: '#7A3BB8' },
  { slug: 'ocean_teal', label: 'Teal', hex: '#0D9488' },
  { slug: 'sunset_orange', label: 'Sunset', hex: '#EA7317' },
  { slug: 'midnight_black', label: 'Midnight', hex: '#1F2430' },
  { slug: 'pearl_white', label: 'Pearl', hex: '#EDEDF0' },
  { slug: 'neon_pink', label: 'Neon Pink', hex: '#F0329C' },
];
import ShareCollectible from '../components/engagement/ShareCollectible';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Easter Egg Reveal Component ────────────────────────────────────────── */

const EasterEggReveal = ({ giftData, onClose }) => {
  const [phase, setPhase] = useState('burst'); // burst -> reveal -> shown
  const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('reveal'), 800);
    const t2 = setTimeout(() => setPhase('shown'), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Generate glitter particles
  const glitterParticles = useRef(
    Array.from({ length: 60 }).map((_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 3,
      size: 3 + Math.random() * 5,
      color: ['#FFD700', '#FF8C00', '#FF69B4', '#7B4FD8', '#2EAA5C', '#2563EB', '#F5831F', '#EC4899'][i % 8],
      wobble: -20 + Math.random() * 40,
      opacity: 0.6 + Math.random() * 0.4,
    }))
  ).current;

  const handleDownload = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/collectibles/welcome-gift/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hibff-founding-collectible.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const handleClaim = async () => {
    try {
      await fetch(`${API_URL}/api/collectibles/welcome-gift/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
    } catch {}
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="easter-egg-reveal">
      {/* Dark backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClaim} />

      {/* Cascading Glitter */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="glitter-container">
        {glitterParticles.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${p.left}%`,
              top: '-10px',
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: p.color,
              opacity: p.opacity,
              boxShadow: `0 0 ${p.size * 2}px ${p.color}80`,
              animation: `glitterFall ${p.duration}s ease-in ${p.delay}s infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes glitterFall {
            0% {
              transform: translateY(-10px) rotate(0deg) scale(1);
              opacity: 0;
            }
            10% {
              opacity: 1;
            }
            50% {
              transform: translateY(50vh) rotate(180deg) scale(0.8);
              opacity: 0.8;
            }
            100% {
              transform: translateY(105vh) rotate(360deg) scale(0.3);
              opacity: 0;
            }
          }
        `}</style>
      </div>

      {/* Light burst */}
      <div className={`absolute inset-0 pointer-events-none transition-opacity duration-700 ${phase === 'burst' ? 'opacity-60' : 'opacity-0'}`}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,200,50,0.6) 0%, rgba(255,165,0,0.2) 40%, transparent 70%)' }} />
      </div>

      {/* Particles */}
      {phase !== 'shown' && Array.from({ length: 20 }).map((_, i) => {
        const angle = (i / 20) * 360;
        const delay = i * 40;
        const dist = 120 + (i % 3) * 60;
        return (
          <div key={i} className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full pointer-events-none"
            style={{
              background: i % 2 === 0 ? '#FFD700' : '#FF8C00',
              opacity: phase === 'burst' ? 1 : 0,
              transform: phase === 'burst'
                ? `translate(-50%,-50%) translate(${Math.cos(angle * Math.PI / 180) * dist}px, ${Math.sin(angle * Math.PI / 180) * dist}px) scale(1)`
                : 'translate(-50%,-50%) scale(0)',
              transition: `all 1.2s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
            }} />
        );
      })}

      {/* Main reveal card */}
      <div className={`relative z-10 transition-all duration-1000 ${phase === 'burst' ? 'scale-50 opacity-0' : 'scale-100 opacity-100'}`}>
        <div className="bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl border border-amber-500/30 shadow-2xl overflow-hidden max-w-sm w-full mx-4"
          style={{ boxShadow: '0 0 60px rgba(255,165,0,0.15), 0 0 120px rgba(255,165,0,0.05)' }}>

          {/* Shimmer top edge */}
          <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, transparent, #FFD700, #FF8C00, #FFD700, transparent)' }} />

          <div className="px-6 pt-6 pb-3 text-center">
            <p className="text-amber-400 text-xs uppercase tracking-[0.25em] font-bold mb-1">A Gift For You</p>
            <h2 className="text-xl font-bold text-white">Someone in your corner is proud of you.</h2>
            <p className="text-sm text-white/60 mt-2">A small token of recognition from your family — for the courage you&apos;ve already shown by starting this journey.</p>
          </div>

          {/* Collectible image */}
          <div className={`px-8 py-3 transition-all duration-1000 delay-500 ${phase === 'shown' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="rounded-xl overflow-hidden ring-2 ring-amber-500/30 shadow-lg">
              {giftData?.image_url ? (
                <img src={giftData.image_url} alt="Your gift" className="w-full h-auto" data-testid="easter-egg-image" />
              ) : (
                <div className="w-full aspect-[3/4] bg-white/5 flex items-center justify-center">
                  <Loader2 size={32} className="text-amber-400 animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className={`px-6 pb-6 pt-3 space-y-2.5 transition-all duration-700 delay-1000 ${phase === 'shown' ? 'opacity-100' : 'opacity-0'}`}>
            <button onClick={handleDownload}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/25 transition-all flex items-center justify-center gap-2"
              data-testid="easter-egg-download">
              <Download size={18} /> Download My Gift
            </button>
            <button onClick={handleClaim}
              className="w-full py-3 bg-white/5 border border-white/10 text-white/70 font-medium rounded-xl hover:bg-white/10 transition-all text-sm"
              data-testid="easter-egg-close">
              Keep in My Vault
            </button>
            <p className="text-center text-[11px] text-white/30 pt-1">
              Yours to keep — a small reminder that someone&apos;s cheering you on.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Hidden Easter Egg Orb ──────────────────────────────────────────────── */

const EasterEggOrb = ({ gift, onReveal }) => {
  if (!gift || gift.claimed) return null;

  return null; // Rendering handled inside VaultDoor now, reveal triggered via onReveal
};

/* ─── Easter Egg Teaser (no claimable gift yet) ───────────────────────────── */
/**
 * Two-state Easter Egg system:
 *   - REWARD state: an unclaimed welcome gift exists → EasterEggReveal modal
 *   - TEASER state: no claimable gift → this lightweight motivational modal
 *
 * The teaser keeps the orb meaningful (it's a teaser, not a broken affordance)
 * and primes future reward moments tied to challenge progress.
 */
const EasterEggTeaser = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="easter-egg-teaser">
      <div className="relative max-w-sm w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl border border-amber-500/30 shadow-2xl overflow-hidden"
             style={{ boxShadow: '0 0 60px rgba(255,165,0,0.15), 0 0 120px rgba(255,165,0,0.05)' }}>
          {/* Shimmer top edge */}
          <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, transparent, #FFD700, #FF8C00, #FFD700, transparent)' }} />

          <div className="px-6 pt-6 pb-3 text-center">
            <p className="text-amber-400 text-xs uppercase tracking-[0.25em] font-bold mb-1">Easter Eggs</p>
            <h2 className="text-xl font-bold text-white">A gift is waiting for the next brave step.</h2>
          </div>

          {/* Static teaser image — same orb asset, slightly larger, no claim flow */}
          <div className="px-10 py-4 flex items-center justify-center">
            <img src="/easter-egg-nft.webp" alt="A gift waiting for you"
                 className="w-32 h-auto opacity-90"
                 style={{ filter: 'drop-shadow(0 0 16px rgba(255,200,50,0.45))' }}
                 data-testid="easter-egg-teaser-image" />
          </div>

          <div className="px-6 pb-2 text-center">
            <p className="text-sm text-white/70 leading-relaxed">
              Gifts unlock as you grow — through challenges met, courage shown,
              and small wins that add up. Keep going. Someone in your corner is
              already proud of you.
            </p>
          </div>

          <div className="px-6 pb-6 pt-4">
            <button onClick={onClose}
              className="w-full py-3 bg-white/5 border border-white/10 text-white/80 font-medium rounded-xl hover:bg-white/10 transition-all text-sm"
              data-testid="easter-egg-teaser-close">
              Got it — back to my vault
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Photorealistic Vault Door ────────────────────────────────────────────── */

const VaultDoor = ({ isOpen, daysRemaining, tenureEligible, hasCombination, vaultVerified, onRequestCode, onVerifyCode, requestingCode, onEasterEggClick }) => {
  // The corner Easter Egg orb is ALWAYS visible — it operates in two
  // states that the parent component selects between via `onEasterEggClick`:
  //   • TEASER  — no claimable welcome gift → motivational modal
  //   • REWARD  — an unclaimed welcome gift exists → real reveal modal
  // Keeping the orb visible at all times preserves its motivational pull
  // ("a gift might be waiting") without ever showing a broken image, since
  // the reveal-vs-teaser branch is decided in the parent before any image
  // is required.
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [activeDigit, setActiveDigit] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const handleDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setError('');
    if (value && index < 5) {
      setActiveDigit(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      setActiveDigit(index - 1);
      inputRefs.current[index - 1]?.focus();
      const newDigits = [...digits];
      newDigits[index - 1] = '';
      setDigits(newDigits);
    }
  };

  const handleSubmitCode = async () => {
    const code = digits.join('');
    if (code.length !== 6) return;
    setVerifying(true);
    setError('');
    const result = await onVerifyCode(code);
    if (!result.success) {
      setError(result.message || 'Incorrect combination');
      setDigits(['', '', '', '', '', '']);
      setActiveDigit(0);
      inputRefs.current[0]?.focus();
    }
    setVerifying(false);
  };

  const showComboEntry = tenureEligible && !vaultVerified;
  const codeComplete = digits.every(d => d !== '');

  return (
    <div className="flex flex-col items-center" data-testid="vault-door">
      {/* Photorealistic vault door with overlay */}
      <div className="relative w-full max-w-md mx-auto">
        <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '1/1' }}>
          {/* Base image */}
          <img
            src="/vault-door.png"
            alt="HiBFF Vault"
            className="w-full h-full object-cover"
            style={{
              filter: isOpen
                ? 'brightness(1.2) saturate(1.1)'
                : 'brightness(0.75) saturate(0.85)',
              transition: 'filter 1.5s ease',
            }}
          />

          {/* Purple/Orange accent overlay */}
          <div className="absolute inset-0" style={{
            background: isOpen
              ? 'radial-gradient(circle at center, rgba(46,170,92,0.15) 0%, rgba(46,170,92,0.05) 50%, transparent 70%)'
              : 'radial-gradient(circle at center, rgba(123,79,216,0.2) 0%, rgba(245,131,31,0.08) 50%, transparent 70%)',
            transition: 'background 1.5s ease',
          }} />

          {/* Vignette */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)',
          }} />

          {/* Center day counter / status */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center" style={{
              background: 'radial-gradient(circle, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, transparent 80%)',
              padding: '40px',
              borderRadius: '50%',
              width: '160px',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {isOpen ? (
                <>
                  <CheckCircle size={36} style={{ color: '#2EAA5C' }} />
                  <p className="text-xs font-semibold mt-2" style={{ color: '#2EAA5C' }}>UNLOCKED</p>
                </>
              ) : showComboEntry ? (
                <>
                  <KeyRound size={28} style={{ color: '#F5831F' }} />
                  <p className="text-sm font-bold mt-1" style={{ color: '#F5831F' }}>ENTER</p>
                  <p className="text-xs" style={{ color: '#F5831F', opacity: 0.8 }}>CODE</p>
                </>
              ) : (
                <>
                  <p className="text-4xl font-black" style={{ color: '#7B4FD8', textShadow: '0 0 20px rgba(123,79,216,0.5)' }}>{daysRemaining}</p>
                  <p className="text-xs font-semibold tracking-widest" style={{ color: '#999' }}>DAYS</p>
                </>
              )}
            </div>
          </div>

          {/* Easter Egg — 45° tilt left, mostly hidden behind corner.
              Always visible: clicking opens REWARD modal if there is an
              unclaimed welcome gift, otherwise the TEASER modal. The
              reveal-vs-teaser decision is made by the parent component
              before any image lookup, so the orb itself never breaks. */}
          <div className="absolute" style={{ zIndex: 5, bottom: '-28px', right: '-22px' }}>
            <div
              onClick={(e) => { e.stopPropagation(); if (onEasterEggClick) onEasterEggClick(); }}
              className="cursor-pointer group"
              data-testid="easter-egg-inner"
            >
              {/* Glow aura on hover */}
              <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: 'radial-gradient(circle, rgba(255,200,50,0.6) 0%, rgba(255,165,0,0.3) 40%, transparent 70%)',
                  transform: 'scale(2)',
                  filter: 'blur(6px)',
                  pointerEvents: 'none',
                }} />
              <img
                src="/easter-egg-nft.webp"
                alt=""
                className="relative transition-all duration-500 group-hover:scale-125"
                style={{ width: '64px', height: 'auto', transform: 'rotate(-45deg)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }}
              />
              <style>{`
                [data-testid="easter-egg-inner"] img {
                  animation: eggWobble 4s ease-in-out infinite;
                }
                [data-testid="easter-egg-inner"]:hover img {
                  animation: eggWobble 1.5s ease-in-out infinite;
                  filter: drop-shadow(0 0 12px rgba(255,200,50,0.7)) drop-shadow(0 0 24px rgba(255,165,0,0.4)) !important;
                }
                @keyframes eggWobble {
                  0%, 100% { transform: rotate(-45deg) translateY(0); }
                  25% { transform: rotate(-43deg) translateY(-2px); }
                  75% { transform: rotate(-47deg) translateY(1px); }
                }
              `}</style>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16" style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
          }} />
        </div>
      </div>

      {/* Status / Combination Entry */}
      <div className="text-center mt-5 w-full max-w-sm" data-testid="vault-status-text">
        {isOpen ? (
          <p className="text-sm font-medium" style={{ color: '#2EAA5C' }}>
            Your vault is open. Your trophies are now yours to trade.
          </p>
        ) : showComboEntry ? (
          <div className="space-y-4">
            <p className="text-sm font-medium" style={{ color: '#F5831F' }}>
              Your vault is ready to open! Enter your 6-digit combination.
            </p>

            {/* 6-digit input */}
            <div className="flex justify-center gap-2" data-testid="combo-input-group">
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  onFocus={() => setActiveDigit(i)}
                  className="w-11 h-14 text-center text-xl font-bold rounded-lg border-2 transition-all outline-none"
                  style={{
                    borderColor: activeDigit === i ? '#F5831F' : digit ? '#7B4FD8' : '#E5E7EB',
                    color: '#1A1209',
                    backgroundColor: digit ? '#FFF7ED' : '#FFF',
                  }}
                  data-testid={`combo-digit-${i}`}
                />
              ))}
            </div>

            {error && <p className="text-xs font-medium text-red-500" data-testid="combo-error">{error}</p>}

            <div className="flex flex-col gap-2">
              <button
                onClick={handleSubmitCode}
                disabled={!codeComplete || verifying}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white text-sm font-semibold disabled:opacity-40 transition-all"
                style={{ backgroundColor: codeComplete ? '#7B4FD8' : '#CCC' }}
                data-testid="combo-submit-btn"
              >
                {verifying ? <><Loader2 size={16} className="animate-spin" /> Verifying...</> : <><KeyRound size={16} /> Unlock Vault</>}
              </button>

              {!hasCombination ? (
                <button
                  onClick={onRequestCode}
                  disabled={requestingCode}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium border transition-all"
                  style={{ borderColor: '#F5831F', color: '#F5831F' }}
                  data-testid="request-code-btn"
                >
                  {requestingCode ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send My Combination</>}
                </button>
              ) : (
                <button
                  onClick={onRequestCode}
                  disabled={requestingCode}
                  className="text-xs underline transition-all"
                  style={{ color: '#999' }}
                  data-testid="resend-code-btn"
                >
                  {requestingCode ? 'Sending...' : 'Resend combination to my phone & email'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm" style={{ color: '#888' }}>
            Your trophies are locked inside. <span className="font-bold" style={{ color: '#7B4FD8' }}>{daysRemaining} days</span> until they&apos;re yours to trade.
          </p>
        )}
      </div>
    </div>
  );
};


/* ─── Main Vault Page ─────────────────────────────────────────────────────── */

const MemeGallery = () => {
  const { user } = useAuth();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(null);
  const [selectedMeme, setSelectedMeme] = useState(null);
  const [mintNotifications, setMintNotifications] = useState([]);
  const [requestingCode, setRequestingCode] = useState(false);
  const [colourPickerFor, setColourPickerFor] = useState(null); // meme awaiting trophy-colour pick
  const [welcomeGift, setWelcomeGift] = useState(null);
  const [nftCollectibles, setNftCollectibles] = useState([]);
  const [gbpBalance, setGbpBalance] = useState(0);
  const [walletTxns, setWalletTxns] = useState([]);
  const [celebrateMint, setCelebrateMint] = useState(null);
  const [showEasterEggReveal, setShowEasterEggReveal] = useState(false);
  const [showEasterEggTeaser, setShowEasterEggTeaser] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadGallery = useCallback(async () => {
    const headers = getHeaders();
    try {
      const [galleryRes, notifRes, giftRes, nftRes] = await Promise.all([
        fetch(`${API_URL}/api/memes/gallery`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/memes/notifications/ready-to-mint`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/collectibles/welcome-gift`, { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/nft/wallet`, { headers }).then(r => r.json()).catch(() => null),
      ]);
      setGallery(galleryRes);
      setMintNotifications(notifRes.ready_to_mint || []);
      if (giftRes && giftRes.has_welcome_gift) setWelcomeGift(giftRes);
      if (nftRes && Array.isArray(nftRes.collectibles)) setNftCollectibles(nftRes.collectibles);
      if (nftRes && typeof nftRes.gbp_balance_pence === 'number') setGbpBalance(nftRes.gbp_balance_pence);
      if (nftRes && Array.isArray(nftRes.wallet_transactions)) setWalletTxns(nftRes.wallet_transactions);
      // Mint celebration — confetti the first time a newly-minted NFT appears.
      try {
        const mintedIds = (galleryRes?.memes || []).filter(m => m.mint_status === 'minted').map(m => m.meme_id);
        const seen = JSON.parse(localStorage.getItem('hibff_seen_minted') || '[]');
        const fresh = mintedIds.filter(id => !seen.includes(id));
        if (seen.length > 0 && fresh.length > 0) {
          const m = (galleryRes?.memes || []).find(x => x.meme_id === fresh[0]);
          setCelebrateMint(m?.title || 'Your NFT');
        }
        localStorage.setItem('hibff_seen_minted', JSON.stringify(mintedIds));
      } catch (e) { /* */ }
    } catch (e) { console.error('Failed to load gallery:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  // Teen ticks a trophy → highlighted green on the Parent Dashboard vault
  // with a green "Mint on Crossmint" button only the parent can action.
  // `trophyColour` is the teen's design pick for THIS trophy (never profile data).
  const toggleMintRequest = async (memeId, trophyColour = undefined) => {
    const headers = { ...getHeaders(), 'Content-Type': 'application/json' };
    try {
      const res = await fetch(`${API_URL}/api/memes/${memeId}/mint-request`, {
        method: 'POST',
        headers,
        body: trophyColour !== undefined ? JSON.stringify({ trophy_colour: trophyColour }) : undefined,
      });
      const data = await res.json();
      if (res.ok) {
        setColourPickerFor(null);
        loadGallery();
        if (selectedMeme?.meme_id === memeId) setSelectedMeme({ ...selectedMeme, mint_requested: data.mint_requested });
      } else {
        alert(data.detail || 'Could not update');
      }
    } catch (e) { alert('Could not update'); }
  };

  // Ticking ON opens the colour picker first; unticking goes straight through.
  const handleTickToMint = (meme) => {
    if (meme.mint_requested) toggleMintRequest(meme.meme_id);
    else setColourPickerFor(meme);
  };

  const mintMeme = async (memeId, chain = 'base') => {
    setMinting(memeId);
    const headers = getHeaders();
    try {
      const res = await fetch(`${API_URL}/api/memes/mint`, {
        method: 'POST', headers,
        body: JSON.stringify({ meme_id: memeId, chain }),
      });
      const data = await res.json();
      if (res.ok) {
        alert(`Minting initiated on ${chain.toUpperCase()}! ${data.message}`);
        loadGallery();
      } else {
        alert(data.detail || 'Minting failed');
      }
    } catch (e) { alert('Minting failed'); }
    setMinting(null);
  };

  // Mark a minted trophy for the parent to execute on Crossmint. The
  // backend gates this behind vault-open verification — see
  // /api/parent/trade-vault/{meme_id}/mark-for-trade.
  const markForTrade = async (memeId) => {
    const headers = getHeaders();
    const priceStr = window.prompt('What price should your parent list it for? (£ — leave blank if unsure)');
    const pence = priceStr ? Math.round(parseFloat(priceStr) * 100) : null;
    try {
      const res = await fetch(`${API_URL}/api/parent/trade-vault/${memeId}/mark-for-trade`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ asking_price_pence: pence && pence > 0 ? pence : null }),
      });
      const data = await res.json();
      if (res.ok) {
        loadGallery();
      } else {
        alert(data.detail || "Couldn't mark for trade.");
      }
    } catch (e) { alert("Couldn't mark for trade."); }
  };

  // Un-mark — moves the trophy back to "keep" state and removes it from
  // the parent's "Run on Crossmint" list.
  const keepTrophy = async (memeId) => {
    const headers = getHeaders();
    try {
      const res = await fetch(`${API_URL}/api/parent/trade-vault/${memeId}/keep`, {
        method: 'POST', headers,
      });
      if (res.ok) loadGallery();
    } catch (e) { /* silent */ }
  };

  const viewMeme = async (memeId) => {
    const headers = getHeaders();
    try {
      const res = await fetch(`${API_URL}/api/memes/${memeId}`, { headers });
      const data = await res.json();
      setSelectedMeme(data);
    } catch (e) { console.error('Failed to load meme:', e); }
  };

  const requestCombination = async () => {
    setRequestingCode(true);
    const headers = getHeaders();
    try {
      const res = await fetch(`${API_URL}/api/memes/vault/combination/send`, { method: 'POST', headers });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'Combination sent!');
        loadGallery();
      } else {
        alert(data.detail || 'Failed to send combination');
      }
    } catch (e) { alert('Failed to request combination'); }
    setRequestingCode(false);
  };

  const verifyCombination = async (code) => {
    const headers = getHeaders();
    try {
      const res = await fetch(`${API_URL}/api/memes/vault/verify`, {
        method: 'POST', headers,
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (res.ok) {
        loadGallery();
        return { success: true };
      } else {
        return { success: false, message: data.detail || 'Incorrect combination' };
      }
    } catch (e) {
      return { success: false, message: 'Verification failed' };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7B4FD8' }} />
        </div>
      </DashboardLayout>
    );
  }

  const isUnlocked = gallery?.can_mint;
  const tenureEligible = gallery?.tenure_eligible;
  const vaultVerified = gallery?.vault_verified;
  const hasCombination = gallery?.has_combination;
  const daysLeft = gallery?.days_until_unlock ?? 180;
  const TROPHY_TARGET = 20;
  const currentTrophies = gallery?.total || 0;
  const trophyProgress = Math.min(100, (currentTrophies / TROPHY_TARGET) * 100);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="meme-gallery-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1A1209' }} data-testid="vault-heading">My Vault</h1>
            <p className="text-sm mt-1" style={{ color: '#666' }}>Complete challenges to earn trophies. Collect 20, unlock your vault, and mint them as NFTs.</p>
          </div>
        </div>

        {/* Trophy Progress Bar */}
        <div className="bg-white rounded-xl p-5 mb-6" style={{ border: '1px solid #E5E7EB' }} data-testid="trophy-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: '#1A1209' }}>Trophy Collection</span>
            <span className="text-sm font-bold" style={{ color: currentTrophies >= TROPHY_TARGET ? '#2EAA5C' : '#7B4FD8' }}>{currentTrophies} / {TROPHY_TARGET}</span>
          </div>
          <div className="w-full h-3 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${trophyProgress}%`,
                background: currentTrophies >= TROPHY_TARGET
                  ? 'linear-gradient(90deg, #2EAA5C, #34D399)'
                  : 'linear-gradient(90deg, #7B4FD8, #9B7FE8)',
              }}
            />
          </div>
          <p className="text-xs mt-2" style={{ color: '#999' }}>
            {currentTrophies >= TROPHY_TARGET
              ? 'Collection complete! Your 20 trophies are ready.'
              : `${TROPHY_TARGET - currentTrophies} more trophies to complete your collection.`}
          </p>
        </div>

        {/* Vault Door Hero */}
        <div className="bg-white rounded-xl p-8 mb-6 relative" style={{ border: '1px solid #E5E7EB' }}>
          <VaultDoor
            isOpen={isUnlocked}
            daysRemaining={daysLeft}
            tenureEligible={tenureEligible}
            hasCombination={hasCombination}
            vaultVerified={vaultVerified}
            onRequestCode={requestCombination}
            onVerifyCode={verifyCombination}
            requestingCode={requestingCode}
            onEasterEggClick={() => {
              // Two-state orb. Real claimable gift → REWARD modal.
              // No claimable gift → TEASER modal (no broken image,
              // no fake claim action, motivational copy only).
              if (welcomeGift && welcomeGift.has_welcome_gift && !welcomeGift.claimed) {
                setShowEasterEggReveal(true);
              } else {
                setShowEasterEggTeaser(true);
              }
            }}
          />
          <EasterEggOrb gift={welcomeGift} />
        </div>

        {/* Easter Egg Reveal Overlay — REWARD state (real unclaimed gift) */}
        {showEasterEggReveal && (
          <EasterEggReveal giftData={welcomeGift} onClose={() => setShowEasterEggReveal(false)} />
        )}

        {/* Easter Egg Teaser Overlay — TEASER state (no claimable gift yet) */}
        {showEasterEggTeaser && (
          <EasterEggTeaser onClose={() => setShowEasterEggTeaser(false)} />
        )}

        {/* Stats Bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Trophies', value: `${currentTrophies} / ${TROPHY_TARGET}`, color: '#7B4FD8', icon: Image },
            { label: 'Ready to Mint', value: gallery?.unminted_count || 0, color: '#F5831F', icon: Zap },
            { label: 'Minted NFTs', value: gallery?.minted_count || 0, color: '#2EAA5C', icon: CheckCircle },
            { label: 'Subscriber Months', value: gallery?.subscriber_months || 0, color: '#2563EB', icon: Clock },
          ].map((stat, i) => (
            <div key={i} className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E5E7EB' }}>
              <div className="flex items-center gap-2 mb-1">
                <stat.icon size={14} style={{ color: stat.color }} />
                <span className="text-xs" style={{ color: '#666' }}>{stat.label}</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* My Wallet — where £ from NFT sales lands */}
        <div className="bg-white p-4 rounded-lg mb-6 flex items-center justify-between gap-4" style={{ border: '1px solid #E5E7EB', background: 'linear-gradient(90deg,#F0FDF4,#FFFFFF)' }} data-testid="teen-wallet-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
              <span className="text-lg" aria-hidden>💷</span>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#666' }}>My Wallet</p>
              <p className="text-xs" style={{ color: '#999' }}>When you trade an NFT, 100% of the money lands here.</p>
            </div>
          </div>
          <p className="text-2xl font-black" style={{ color: '#16A34A' }} data-testid="teen-wallet-balance">
            £{(gbpBalance / 100).toFixed(2)}
          </p>
        </div>
        {walletTxns.length > 0 && (
          <div className="bg-white rounded-lg mb-6 -mt-4 px-4 py-3" style={{ border: '1px solid #E5E7EB' }} data-testid="wallet-transactions">
            {walletTxns.slice(0, 5).map(t => (
              <div key={t.txn_id} className="flex items-center justify-between py-1.5 text-xs" style={{ borderBottom: '1px dashed #F3F4F6' }}>
                <span style={{ color: '#666' }}>{t.reason}</span>
                <span className="font-bold" style={{ color: t.direction === 'credit' ? '#16A34A' : '#DC2626' }}>
                  {t.direction === 'credit' ? '+' : '−'}£{(t.amount_pence / 100).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Mint celebration overlay */}
        {celebrateMint && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(10,5,30,0.75)' }} data-testid="mint-celebration" onClick={() => setCelebrateMint(null)}>
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl" style={{ animation: 'pop-in .4s ease' }}>
              <div className="text-6xl mb-3" aria-hidden>🎉</div>
              <h3 className="text-xl font-black mb-2" style={{ color: '#1A1209' }}>It&apos;s official — you own an NFT!</h3>
              <p className="text-sm mb-5" style={{ color: '#666' }}><strong>{celebrateMint}</strong> has been minted on the blockchain and is locked safely in your 180-day Vault.</p>
              <button onClick={() => setCelebrateMint(null)} className="px-8 py-3 rounded-xl text-white font-bold" style={{ backgroundColor: '#7B4FD8' }} data-testid="mint-celebration-close">
                Amazing!
              </button>
            </div>
            <style>{'@keyframes pop-in{0%{transform:scale(.7);opacity:0}100%{transform:scale(1);opacity:1}}'}</style>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <Award size={16} style={{ color: '#F5831F' }} />
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }} data-testid="show-cards-heading">Show Cards — Challenges 1–9</h3>
          <span className="text-xs" style={{ color: '#999' }}>Your unique AI-generated trophies, on display from day one</span>
        </div>
        {(gallery?.memes || []).length === 0 ? (
          <div className="bg-white rounded-xl p-16 text-center" style={{ border: '1px solid #E5E7EB' }}>
            <Award size={56} className="mx-auto mb-4" style={{ color: '#DDD' }} />
            <h3 className="text-lg font-bold mb-2" style={{ color: '#333' }}>Your Vault is Empty</h3>
            <p className="text-sm mb-6" style={{ color: '#999' }}>Complete real-world challenges with your AvA to earn achievement trophies. Your first 12 challenge rewards arrive as unique AI-generated show cards automatically — no action needed. From challenge 13, every reward is a mintable NFT.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="meme-grid">
            {gallery.memes.map((meme) => {
              const isLocked = !isUnlocked;
              return (
                <div
                  key={meme.meme_id}
                  className="bg-white rounded-lg overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-200"
                  style={{ border: '1px solid #E5E7EB' }}
                  onClick={() => viewMeme(meme.meme_id)}
                  data-testid={`meme-card-${meme.meme_id}`}
                >
                  <div className="aspect-square relative overflow-hidden" style={{ background: '#1A1209' }}>
                    {meme.generation_status === 'processing' ? (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <Loader2 size={32} className="animate-spin mb-2" style={{ color: '#7B4FD8' }} />
                        <span className="text-xs text-white/60">Generating...</span>
                      </div>
                    ) : meme.has_image ? (
                      // Founder spec: show-card trophies (challenges 1–9) display
                      // their unique custom artwork immediately — seeing the image
                      // is the encouragement that powers the run to mintable NFTs.
                      <img
                        src={`${API_URL}/api/memes/${meme.meme_id}/image`}
                        alt={meme.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          // Image failed to load — swap to gradient.
                          const wrap = e.currentTarget.parentElement;
                          if (wrap && !wrap.dataset.fallbackApplied) {
                            wrap.dataset.fallbackApplied = 'true';
                            e.currentTarget.style.display = 'none';
                            const div = document.createElement('div');
                            div.className = 'w-full h-full flex flex-col items-center justify-center text-white';
                            div.style.background = 'linear-gradient(135deg, #7B4FD8 0%, #F5831F 100%)';
                            div.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.95;margin-bottom:6px"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg><span style="font-size:11px;opacity:0.85;text-align:center;padding:0 8px">Trophy artwork unavailable</span>`;
                            wrap.appendChild(div);
                          }
                        }}
                      />
                    ) : (
                      // No artwork available yet (generation failed or legacy doc).
                      <div
                        className="w-full h-full flex flex-col items-center justify-center text-white relative"
                        style={{ background: 'linear-gradient(135deg, #7B4FD8 0%, #F5831F 100%)' }}
                        data-testid="trophy-sealed-preview"
                      >
                        <div className="absolute inset-0" style={{
                          background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 75% 75%, rgba(0,0,0,0.18), transparent 50%)',
                          pointerEvents: 'none',
                        }} />
                        <div className="relative bg-black/25 rounded-full p-3 backdrop-blur-sm mb-2">
                          <Award size={22} style={{ color: '#FFF' }} />
                        </div>
                        <span className="relative text-[11px] text-center px-3 font-semibold" style={{ opacity: 0.95, letterSpacing: '0.02em' }}>
                          Artwork on its way
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      {meme.mint_status === 'minted' ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white"><CheckCircle size={10} /> NFT</span>
                      ) : meme.status === 'ready_to_mint' ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: '#7B4FD8' }}><Zap size={10} /> Mintable</span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: '#F5831F' }}><Award size={10} /> Reward</span>
                      )}
                    </div>
                    {meme.type === 'loyalty' && (
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#1A1209' }}>LOYALTY</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-bold truncate" style={{ color: '#1A1209' }}>{meme.title}</h4>
                    <p className="text-xs truncate mt-0.5" style={{ color: '#666' }}>{meme.caption}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs" style={{ color: '#999' }}>{meme.created_at?.slice(0, 10)}</span>
                      {meme.mint_status !== 'minted' && meme.mint_status !== 'pending' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTickToMint(meme); }}
                          className="text-xs px-2 py-1 rounded font-semibold"
                          style={meme.mint_requested
                            ? { backgroundColor: '#16A34A', color: '#FFF', fontSize: '10px' }
                            : { backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB', fontSize: '10px' }}
                          data-testid={`tick-mint-${meme.meme_id}`}
                        >
                          {meme.mint_requested ? '✓ Ticked — parent will mint' : 'Tick to mint'}
                        </button>
                      )}
                      {meme.mint_status === 'minted' && meme.nft_chain && (
                        // Sprint 207f — purely-static chain indicator. No cursor,
                        // no hover, no onClick. Clarifies that this is metadata,
                        // not an interactive control.
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-md"
                          style={{
                            padding: '2px 6px',
                            color: '#4F4A65',
                            background: '#F3F1FA',
                            border: '1px solid #E2DCF2',
                            cursor: 'default',
                            userSelect: 'none',
                          }}
                          data-testid={`chain-label-${meme.meme_id}`}
                          aria-label={`Minted on ${meme.nft_chain}`}
                        >
                          <span
                            className="inline-block rounded-full"
                            style={{ width: 6, height: 6, background: '#7B4FD8' }}
                          />
                          {meme.nft_chain}
                        </span>
                      )}
                    </div>

                    {/* Sprint 207f — Independent on-chain verification.
                        Read-only outbound links to Polygonscan + OpenSea so
                        the teen can prove their NFT exists on Polygon mainnet.
                        Does NOT grant any trading capability — that stays
                        gated behind the parent-led /mark-for-trade flow. */}
                    {meme.mint_status === 'minted' && (meme.nft_contract && meme.nft_token_id) && (
                      <div
                        className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
                        data-testid={`onchain-links-${meme.meme_id}`}
                      >
                        <a
                          href={
                            meme.mint_tx_hash
                              ? `https://polygonscan.com/tx/${meme.mint_tx_hash}`
                              : `https://polygonscan.com/token/${meme.nft_contract}?a=${meme.nft_token_id}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 font-medium hover:underline min-h-[36px] py-1.5"
                          style={{ color: '#7B4FD8' }}
                          data-testid={`polygonscan-link-${meme.meme_id}`}
                          title="Verify this trophy on Polygonscan"
                        >
                          🔗 View on Polygonscan
                        </a>
                        <a
                          href={`https://opensea.io/assets/matic/${meme.nft_contract}/${meme.nft_token_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 font-medium hover:underline min-h-[36px] py-1.5"
                          style={{ color: '#2081E2' }}
                          data-testid={`opensea-link-${meme.meme_id}`}
                          title="View this trophy on OpenSea"
                        >
                          🐳 View on OpenSea
                        </a>
                      </div>
                    )}
                    {/* Trade / Keep toggle — only shown once the vault has
                        been opened (isUnlocked=true) AND the meme is minted
                        as an NFT. Marks the NFT for the parent to run on
                        Crossmint (legally compliant teen-consent flow). */}
                    {isUnlocked && meme.mint_status === 'minted' && (
                      <div className="mt-2 pt-2 border-t" style={{ borderColor: '#F3E8DC' }}>
                        {meme.marked_for_trade ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); keepTrophy(meme.meme_id); }}
                            data-testid={`keep-trophy-${meme.meme_id}`}
                            className="w-full text-[10px] font-bold px-2 py-1.5 rounded flex items-center justify-center gap-1"
                            style={{ backgroundColor: '#FFF5EA', color: '#A65812', border: '1px solid #F5831F40' }}
                          >
                            <Heart size={10} /> Keep this one (un-mark)
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); markForTrade(meme.meme_id); }}
                            data-testid={`mark-for-trade-${meme.meme_id}`}
                            className="w-full text-[10px] font-bold px-2 py-1.5 rounded text-white flex items-center justify-center gap-1"
                            style={{ background: 'linear-gradient(135deg, #F5831F, #FFB073)' }}
                          >
                            <ExternalLink size={10} /> Ask parent to trade
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Mintable NFT Collection — challenges 10 onward (custodial vault) */}
        <div className="mt-10" data-testid="nft-collection-section">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={16} style={{ color: '#7B4FD8' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }} data-testid="nft-collection-heading">Mintable NFT Collection — Challenge 10 onwards</h3>
            <span className="text-xs" style={{ color: '#999' }}>Real NFTs held safely in your Vault — mintable when your Vault opens</span>
          </div>
          {nftCollectibles.length === 0 ? (
            <div className="bg-white rounded-xl p-10 text-center" style={{ border: '1px dashed #D8CFF0' }}>
              <Zap size={40} className="mx-auto mb-3" style={{ color: '#CBB8F5' }} />
              <p className="text-sm font-semibold mb-1" style={{ color: '#4F4A65' }}>The Pivotal Moment awaits</p>
              <p className="text-xs" style={{ color: '#999' }}>Complete challenge 10 to unlock your first real NFT — from there, every challenge mints a unique NFT straight into your Vault.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="nft-grid">
              {nftCollectibles.map((col) => (
                <div
                  key={col.collectible_id}
                  className="bg-white rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200"
                  style={{ border: '1px solid #E5E7EB' }}
                  data-testid={`nft-card-${col.collectible_id}`}
                >
                  <div className="aspect-square relative overflow-hidden" style={{ background: '#14101F' }}>
                    {col.image_url ? (
                      <img
                        src={col.image_url}
                        alt={col.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Zap size={36} style={{ color: '#7B4FD8' }} />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      {col.status === 'success' ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white"><CheckCircle size={10} /> Minted</span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: '#7B4FD8' }}><Lock size={10} /> In Vault</span>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-bold truncate" style={{ color: '#1A1209' }}>{col.name}</h4>
                    <p className="text-xs truncate mt-0.5" style={{ color: '#666' }}>{col.description}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs" style={{ color: '#999' }}>{col.minted_at?.slice(0, 10)}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#7B4FD8' }}>{col.chain || 'polygon'}</span>
                    </div>
                    {col.status !== 'success' && (
                      <p className="text-[10px] mt-2" style={{ color: '#999' }}>
                        {isUnlocked ? 'Ready — mint from your wallet page.' : 'Mintable when your Vault opens (180 days).'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedMeme && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setSelectedMeme(null)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="meme-detail-modal">
              <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
                <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>{selectedMeme.title}</h3>
                <button onClick={() => setSelectedMeme(null)}><X size={18} style={{ color: '#666' }} /></button>
              </div>
              <div className="p-5">
                {selectedMeme.image_data ? (
                  <img src={`data:${selectedMeme.mime_type || 'image/png'};base64,${selectedMeme.image_data}`} alt={selectedMeme.title} className="w-full rounded-lg mb-4" />
                ) : selectedMeme.image_url ? (
                  <img src={`${API_URL}/api/memes/${selectedMeme.meme_id}/image`} alt={selectedMeme.title} className="w-full rounded-lg mb-4" />
                ) : (
                  // Sealed preview — reveal happens on mint. Consistent with
                  // the grid's gradient placeholder so the "mint to unveil"
                  // story holds end-to-end.
                  <div
                    className="w-full aspect-square rounded-lg mb-4 flex flex-col items-center justify-center text-white relative overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #7B4FD8 0%, #F5831F 100%)' }}
                    data-testid="trophy-sealed-detail"
                  >
                    <div className="absolute inset-0" style={{
                      background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 75% 75%, rgba(0,0,0,0.18), transparent 50%)',
                      pointerEvents: 'none',
                    }} />
                    <div className="relative bg-black/25 rounded-full p-5 backdrop-blur-sm mb-3">
                      <Lock size={40} style={{ color: '#FFF' }} />
                    </div>
                    <span className="relative text-sm font-bold mb-1" style={{ letterSpacing: '0.02em' }}>Artwork on its way</span>
                    <span className="relative text-xs px-6 text-center" style={{ opacity: 0.9 }}>
                      Your unique trophy artwork is still being created — check back shortly.
                    </span>
                  </div>
                )}
                <p className="text-sm mb-3" style={{ color: '#333' }}>{selectedMeme.caption}</p>
                <div className="flex items-center gap-4 text-xs mb-4" style={{ color: '#999' }}>
                  <span>Created: {selectedMeme.created_at?.slice(0, 10)}</span>
                  <span>Type: {selectedMeme.type}</span>
                  {selectedMeme.challenge_title && <span>Challenge: {selectedMeme.challenge_title}</span>}
                </div>
                <div className="flex gap-3">
                  {selectedMeme.mint_status === 'minted' ? (
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>
                      <CheckCircle size={16} /> Minted on {selectedMeme.nft_chain?.toUpperCase() || 'blockchain'}
                    </div>
                  ) : selectedMeme.mint_status === 'pending' ? (
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: '#FFF7ED', color: '#C2410C' }}>
                      <Loader2 size={16} className="animate-spin" /> Minting in progress...
                    </div>
                  ) : (
                    <button
                      onClick={() => handleTickToMint(selectedMeme)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium"
                      style={selectedMeme.mint_requested
                        ? { backgroundColor: '#16A34A', color: '#FFF' }
                        : { backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB' }}
                      data-testid="modal-tick-mint"
                    >
                      {selectedMeme.mint_requested
                        ? <><CheckCircle size={14} /> Ticked — your parent will mint this</>
                        : <><Zap size={14} /> Tick to mint — your parent actions it</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Trophy colour picker — the teen's design choice for THIS trophy */}
        {colourPickerFor && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="trophy-colour-picker" onClick={() => setColourPickerFor(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-1" style={{ color: '#1A1209' }}>Pick your trophy colour</h3>
              <p className="text-xs mb-4" style={{ color: '#666' }}>Your choice becomes the accent colour of this trophy's final art.</p>
              <div className="grid grid-cols-4 gap-3 mb-4">
                {TROPHY_COLOURS.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => toggleMintRequest(colourPickerFor.meme_id, c.slug)}
                    className="flex flex-col items-center gap-1 group"
                    data-testid={`trophy-colour-${c.slug}`}
                    title={c.label}
                  >
                    <span className="w-9 h-9 rounded-full border-2 border-transparent group-hover:border-gray-800 transition-colors" style={{ background: c.hex }} />
                    <span className="text-[9px] leading-tight text-center" style={{ color: '#555' }}>{c.label}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleMintRequest(colourPickerFor.meme_id, null)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB' }}
                  data-testid="trophy-colour-surprise"
                >
                  Surprise me
                </button>
                <button
                  onClick={() => setColourPickerFor(null)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ backgroundColor: '#FFF', color: '#999', border: '1px solid #E5E7EB' }}
                  data-testid="trophy-colour-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Leaderboard & Marketplace */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <VaultLeaderboard />
          <TradingMarketplace />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MemeGallery;
